import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useLanguage } from "@/context/LanguageContext";

const FILTERS = [
    { id: null, key: "all" },
    { id: "giacche", key: "giacche" },
    { id: "hoodies", key: "hoodies" },
    { id: "completi", key: "completi" },
    { id: "top", key: "top" },
];

export default function ShopPage() {
    const { t } = useLanguage();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeFilter = searchParams.get("categoria");

    useEffect(() => {
        setLoading(true);
        fetchProducts(activeFilter)
            .then(setProducts)
            .catch(() => setProducts([]))
            .finally(() => setLoading(false));
    }, [activeFilter]);

    return (
        <main className="pt-16 md:pt-20 min-h-screen" data-testid="shop-page">
            <div className="px-5 md:px-10 py-16 md:py-24">
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="font-display text-5xl md:text-7xl font-extrabold uppercase tracking-tighter"
                >
                    {t("shop.title")}
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mt-4 text-sm text-zinc-500 max-w-md"
                >
                    {t("shop.subtitle")}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-10 flex flex-wrap gap-3"
                    data-testid="shop-filters"
                >
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setSearchParams(f.id ? { categoria: f.id } : {})}
                            data-testid={`filter-${f.id || "tutti"}`}
                            className={`px-6 py-3 text-xs tracking-[0.25em] uppercase font-semibold border transition-colors duration-300 ${
                                activeFilter === f.id || (!activeFilter && !f.id)
                                    ? "bg-black text-white border-black"
                                    : "border-zinc-300 hover:border-black"
                            }`}
                        >
                            {t(`shop.filters.${f.key}`)}
                        </button>
                    ))}
                </motion.div>

                {loading ? (
                    <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {[1, 2, 3].map((n) => (
                            <div key={n} className="animate-pulse">
                                <div className="bg-zinc-100 aspect-[3/4]" />
                                <div className="mt-4 h-4 bg-zinc-100 w-2/3" />
                            </div>
                        ))}
                    </div>
                ) : products.length === 0 ? (
                    <div className="mt-24 text-center" data-testid="shop-empty-state">
                        <p className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
                            {t("shop.emptyTitle")}
                        </p>
                        <p className="mt-3 text-sm text-zinc-500">
                            {t("shop.emptyBody")}
                        </p>
                    </div>
                ) : (
                    <div
                        className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
                        data-testid="shop-product-grid"
                    >
                        {products.map((product, i) => (
                            <ProductCard key={product.id} product={product} index={i} />
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}
